// Module-scoped Tesseract worker: a single Tesseract.Worker per session,
// lazy-init on first `ensureTesseract()` call, terminated on beforeunload.
// Persists across scan-screen mounts and across ActiveFormat switches so
// toggling between QR/ArUco/Text doesn't re-pay the ~4MB traineddata
// download.
//
// Status is exposed via a lightweight event surface so the scan page can
// render an "Initializing OCR…" state during cold-start and revert the
// selected format to 'qr' + toast on failure (offline, CDN 404, corrupted
// cache).

import {
  createWorker,
  PSM,
  type ImageLike,
  type OutputFormats,
  type RecognizeResult,
  type Worker,
} from 'tesseract.js';

export type TesseractStatus =
  | { state: 'idle' }
  | { state: 'initializing' }
  | { state: 'ready' }
  | { state: 'failed'; message: string };

let workerPromise: Promise<Worker> | null = null;
let status: TesseractStatus = { state: 'idle' };
const listeners = new Set<(s: TesseractStatus) => void>();

// Cache last-applied whitelist so we only re-issue setParameters when the
// user actually changes the pattern (setParameters round-trips to the
// worker thread and adds ~5-10ms of unnecessary latency per frame).
let currentWhitelist: string | undefined = undefined;
let whitelistApplied = false;

function setStatus(next: TesseractStatus): void {
  status = next;
  for (const cb of listeners) cb(next);
}

export function getTesseractStatus(): TesseractStatus {
  return status;
}

export function subscribeTesseractStatus(cb: (s: TesseractStatus) => void): () => void {
  listeners.add(cb);
  cb(status);
  return () => {
    listeners.delete(cb);
  };
}

export function tesseractStatusLabel(s: TesseractStatus): string {
  switch (s.state) {
    case 'idle':
      return 'OCR loads on first use (~4 MB)';
    case 'initializing':
      return 'initializing OCR…';
    case 'ready':
      return 'detector ready';
    case 'failed':
      return `error: ${s.message}`;
  }
}

export function tesseractStatusColor(s: TesseractStatus): string {
  switch (s.state) {
    case 'ready':
      return '#f59e0b'; // amber — matches the text bbox color
    case 'failed':
      return '#f87171'; // red-400
    case 'initializing':
    case 'idle':
    default:
      return '#9ca3af'; // gray-400
  }
}

export function ensureTesseract(): Promise<Worker> {
  if (workerPromise) return workerPromise;
  setStatus({ state: 'initializing' });
  workerPromise = (async () => {
    try {
      const worker = await createWorker('eng');
      // SPARSE_TEXT finds text anywhere in the frame without assuming a
      // single-line layout — the right mode for wall-scanning where each
      // bin label is small text among lots of surrounding content.
      // SINGLE_LINE (7) works only when the caller has pre-cropped to
      // one line; on a full webcam frame it silently misses hits.
      await worker.setParameters({
        tessedit_pageseg_mode: PSM.SPARSE_TEXT,
      });
      setStatus({ state: 'ready' });
      return worker;
    } catch (err) {
      workerPromise = null;
      whitelistApplied = false;
      const message = err instanceof Error ? err.message : String(err);
      setStatus({ state: 'failed', message });
      throw err;
    }
  })();
  return workerPromise;
}

// Serialize whitelist changes + recognize calls through a single Promise
// chain so a fast-swap between two patterns can't produce interleaved
// setParameters(A) → setParameters(B) → recognize(A) → recognize(B).
// The tesseract.js worker itself is FIFO, but interleaving the two
// separate calls across concurrent decoders would run A's recognize
// against B's whitelist.
let workerChain: Promise<unknown> = Promise.resolve();

export function runRecognizeWithWhitelist(
  image: ImageLike,
  whitelist: string | undefined,
  output?: Partial<OutputFormats>
): Promise<RecognizeResult> {
  const next = workerChain.then(async () => {
    const worker = await ensureTesseract();
    if (!whitelistApplied || currentWhitelist !== whitelist) {
      await worker.setParameters({
        tessedit_char_whitelist: whitelist ?? '',
      });
      currentWhitelist = whitelist;
      whitelistApplied = true;
    }
    return worker.recognize(image, {}, output);
  });
  // Keep the chain alive on rejection so the next enqueue doesn't inherit a
  // rejected promise (which would poison every subsequent decode).
  workerChain = next.catch(() => undefined);
  return next;
}

if (typeof window !== 'undefined') {
  // pagehide beats beforeunload — mobile Safari misses beforeunload on
  // background tabs, and pagehide fires reliably on both back/forward
  // cache and true unloads.
  window.addEventListener('pagehide', () => {
    if (!workerPromise) return;
    workerPromise.then((w) => w.terminate()).catch(() => undefined);
  });
}
